import { TenantName, AVAILABLE_TENANTS } from '../types/tenant.types';

export class TenantResolver {
  private static instance: TenantResolver;
  private currentTenant: TenantName | null = null;

  private constructor() {}

  static getInstance(): TenantResolver {
    if (!TenantResolver.instance) {
      TenantResolver.instance = new TenantResolver();
    }
    return TenantResolver.instance;
  }

  static resolveTenant(): TenantName {
    return TenantResolver.getInstance().resolveTenant();
  }

  resolveTenant(): TenantName {
    if (this.currentTenant) return this.currentTenant;

    // 1. Subdomain (PRODUCTION)
    const subdomain = this.getTenantFromHostname();
    if (subdomain && this.isValidTenant(subdomain)) {
      this.currentTenant = subdomain as TenantName;
      return this.currentTenant;
    }

    // 2. Query param (DEV / fallback)
    const urlParams = new URLSearchParams(window.location.search);
    const appParam = urlParams.get('app');
    if (appParam && this.isValidTenant(appParam)) {
      this.currentTenant = appParam as TenantName;
      return this.currentTenant;
    }

    // 3. Dev-only env fallback
    const envProject = process.env.REACT_APP_PROJECT;
    if (envProject && this.isValidTenant(envProject)) {
      this.currentTenant = envProject as TenantName;
      return this.currentTenant;
    }

    // 4. Hard fallback
    this.currentTenant = 'citysense_combined';
    console.warn('No tenant resolved, defaulting to citysense');
    return this.currentTenant;
  }

  private getTenantFromHostname(): string | null {
    const hostname = window.location.hostname.toLowerCase();

    // IGNORE LOCALHOST & IP
    if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return null;
    }

    const parts = hostname.split('.');

    if (parts.length >= 3) {
      return parts[0];
    }

    if (parts.length === 2 && parts[1] === 'local') {
      return parts[0];
    }

    return null;
  }

  private isValidTenant(tenant: string): boolean {
    return AVAILABLE_TENANTS.includes(tenant as TenantName);
  }

  getCurrentTenant(): TenantName {
    return this.currentTenant ?? this.resolveTenant();
  }

  setTenant(tenant: TenantName): void {
    if (!this.isValidTenant(tenant)) {
      throw new Error(`Invalid tenant: ${tenant}`);
    }
    this.currentTenant = tenant;
  }
}

export const tenantResolver = TenantResolver.getInstance();
